if (!window.Maximize.loadedScript.includes("quick-order-list.js")) {
  window.Maximize.loadedScript.push("quick-order-list.js");

  requestAnimationFrame(() => {
    if (!window.formatMoney) {
      window.formatMoney = function (amount, formatString) {
        const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
        const fmt = (n, precision, thousands = ",", decimal = ".") => {
          if (isNaN(n) || n == null) return 0;
          n = (n / 100.0).toFixed(precision);
          const [dollars, cents] = n.split(".");
          return dollars.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands) + (cents ? decimal + cents : "");
        };
        let value;
        switch (formatString.match(placeholderRegex)[1]) {
          case "amount":
            value = fmt(amount, 2);
            break;
          case "amount_no_decimals":
            value = fmt(amount, 0);
            break;
          case "amount_with_comma_separator":
            value = fmt(amount, 2, ".", ",");
            break;
          case "amount_no_decimals_with_comma_separator":
            value = fmt(amount, 0, ".", ",");
            break;
        }
        return formatString.replace(placeholderRegex, value);
      };
    }

    class QuickOrderList extends HTMLElement {
      constructor() {
        super();
        this.moneyFormat = this.dataset.moneyFormat;
        this.cartItems = [];
        this.totalItems = 0;
        this.subtotal = 0;
        this.isUpdating = false;
        this.removeAllConfirm = false;
        this.removeAllBursting = false;
        this.isRemovingAll = false;
        this.errorMessages = {}; // server errors → inline toast
        this.showSuccess = {};
        this.successTimers = {};
        this._qtyRuleErrorTimers = {}; // variantId → auto-hide timer for the quantity-rule popup
        this.productVariants = [];
        this.currentPage = 1;
        this.totalPages = 1;
        this.variantsPerPage = 50;
        this._targetQty = {};
        this._qtyChains = {};
        this._debouncedQtyFlush = {};
        this._activeQtyUpdates = 0;
        this._updatingVariants = new Set();
        this._pendingVariants = new Set(); // 714: total held back until the API confirms
        this.serverQty = {};
        this.variantPreorder = {};
        this._removeAllBurstTimer = null;
        this._removeAllConfirmRevealTimer = null;
        this._onCartUpdated = () => {
          if (this.isUpdating || this._activeQtyUpdates > 0) return;
          this.fetchCart();
        };
        this._onDocumentClick = this._handleDocumentClick.bind(this);
      }

      connectedCallback() {
        const variantsEl = this.querySelector(".qol-variants-json");
        if (variantsEl) {
          try {
            this.productVariants = JSON.parse(variantsEl.textContent);
          } catch (e) {}
        }

        // quantity_price_breaks is absent from Shopify's default variant JSON, so it is
        // emitted separately (keyed by variant id) and merged here for volume pricing.
        const priceBreaksEl = this.querySelector(".qol-price-breaks-json");
        if (priceBreaksEl) {
          try {
            const breaksById = JSON.parse(priceBreaksEl.textContent);
            this.productVariants.forEach((v) => {
              v.priceBreaks = breaksById[v.id] || [];
            });
          } catch (e) {}
        }

        this.variantsPerPage = parseInt(this.dataset.variantsPerPage, 10) || 50;

        this.totalPages = Math.ceil(this.productVariants.length / this.variantsPerPage) || 1;

        const cartItemsEl = this.querySelector(".qol-cart-items-json");
        if (cartItemsEl) {
          try {
            this.cartItems = JSON.parse(cartItemsEl.textContent);
            this.syncServerQty(this.cartItems);
            this.calculateTotals();
          } catch (e) {}
        }

        this.querySelectorAll('.qol-row[data-preorder="true"]').forEach((row) => {
          this.variantPreorder[parseInt(row.dataset.variantId, 10)] = true;
        });

        this._buildPagination();
        this._bindEvents();
        this._renderAll();
        // this.fetchCart();

        window.addEventListener(`${CART_EVENT.cartUpdate}`, this._onCartUpdated);
        window.addEventListener(`${CART_EVENT.cartItemUpdateFail}`, this._onCartUpdated);
        document.addEventListener("click", this._onDocumentClick);
      }

      disconnectedCallback() {
        window.removeEventListener(`${CART_EVENT.cartUpdate}`, this._onCartUpdated);
        window.removeEventListener(`${CART_EVENT.cartItemUpdateFail}`, this._onCartUpdated);
        document.removeEventListener("click", this._onDocumentClick);
      }

      _bindEvents() {
        this.addEventListener("click", (e) => {
          const decrement = e.target.closest(".qol-qty-decrement");
          const increment = e.target.closest(".qol-qty-increment");
          const remove = e.target.closest(".qol-remove-btn");
          const viewCart = e.target.closest(".qol-view-cart-btn");
          const removeAllBtn = e.target.closest(".qol-remove-all-link");
          const confirmRemove = e.target.closest(".qol-confirm-btn");
          const cancelRemove = e.target.closest(".qol-cancel-link");
          const paginationLink = e.target.closest(".qol-pagination a[href]");
          const errorEl = e.target.closest(".flash-message.error-message");

          if (decrement) {
            this._stepQty(parseInt(decrement.dataset.variantId, 10), -1);
          } else if (increment) {
            this._stepQty(parseInt(increment.dataset.variantId, 10), 1);
          } else if (remove) {
            const id = parseInt(remove.dataset.variantId, 10);
            if (!this._updatingVariants.has(id) && !this.isRemovingAll) this.updateQty(id, 0);
          } else if (viewCart) {
            this.viewCart();
          } else if (removeAllBtn) {
            this.onRemoveAllClick();
          } else if (confirmRemove) {
            this.removeAll();
          } else if (cancelRemove) {
            this.closeRemoveAllConfirm();
          } else if (paginationLink) {
            e.preventDefault();
            this._navigatePage(paginationLink.href);
          } else if (errorEl) {
            this._hideToast(parseInt(errorEl.dataset.variantId, 10));
          }
        });

        this.addEventListener("keyup", (e) => {
          if (e.key !== "Enter") return;
          const decrement = e.target.closest(".qol-qty-decrement");
          const increment = e.target.closest(".qol-qty-increment");
          if (decrement) {
            this._stepQty(parseInt(decrement.dataset.variantId, 10), -1);
          } else if (increment) {
            this._stepQty(parseInt(increment.dataset.variantId, 10), 1);
          }
        });

        this.addEventListener("change", (e) => {
          const input = e.target.closest(".qol-qty-input");
          if (input) {
            const id = parseInt(input.dataset.variantId, 10);
            if (!this._updatingVariants.has(id) && !this.isRemovingAll) this._commitTypedQty(id, input);
          }
        });
      }

      // Step by one increment in the pressed direction, honouring the variant's quantity rule.
      // The buttons used to add/subtract a flat 1, which for an increment-of-5 product produced
      // a quantity Shopify then rejected.
      _stepQty(variantId, delta) {
        if (Number.isNaN(variantId)) return;
        if (this._updatingVariants.has(variantId) || this.isRemovingAll) return;

        const rule = this._ruleFor(variantId);
        const currentQty = this.getVariantQty(variantId);

        if (delta < 0) {
          if (currentQty <= 0) return;
          const next = quantityRuleUtils.step(currentQty, rule, -1);
          // step() clamps at the minimum, so it failing to lower the value means we were already at
          // the floor — and nothing between 0 and the minimum is orderable, so remove the line.
          this._clearQtyRuleError(variantId);
          this.updateQty(variantId, next >= currentQty ? 0 : next);
          return;
        }

        const max = this.getVariantMaxQty(variantId);
        if (currentQty >= max) return;
        // The first press has to reach the minimum, not 1.
        const next = currentQty <= 0 ? rule.min : quantityRuleUtils.step(currentQty, rule, 1);
        this._clearQtyRuleError(variantId);
        this.updateQty(variantId, Math.min(next, max));
      }

      // Quantity-rule error → the popup above the row's quantity box. Deliberately a different
      // surface from `errorMessages`, which carries SERVER errors in the inline toast: a rule
      // message and a cart error used to overwrite one another. Same contract as the cart's
      // _addQtyError — passing autoHideMs clears it after the delay, and any newer message or
      // clear cancels a pending hide.
      _addQtyRuleError(variantId, message, autoHideMs) {
        const el = document.getElementById(`QolQtyRuleError-${variantId}`);
        if (!el) return;
        clearTimeout(this._qtyRuleErrorTimers[variantId]);
        const textEl = el.getElementsByClassName("qol-qty-error-text")[0];
        if (textEl) textEl.innerHTML = message;
        if (message && autoHideMs) {
          this._qtyRuleErrorTimers[variantId] = setTimeout(() => this._clearQtyRuleError(variantId), autoHideMs);
        }
      }

      _clearQtyRuleError(variantId) {
        const el = document.getElementById(`QolQtyRuleError-${variantId}`);
        if (!el) return;
        clearTimeout(this._qtyRuleErrorTimers[variantId]);
        const textEl = el.getElementsByClassName("qol-qty-error-text")[0];
        if (textEl) textEl.innerHTML = ""; // empty content closes the popup (MutationObserver)
      }

      // Same policy as the cart (see cart.js validateItemQty): a value OUT OF RANGE — blank, not a
      // number, below min, above max — is corrected, committed, and the reason shown for 3s. A value
      // that is in range but off the increment grid keeps a persistent message and is NOT committed,
      // so the shopper can pick one of the two valid values the message names. 0 still means remove.
      _commitTypedQty(variantId, inputEl) {
        // Without a B2B catalog there is no rule to snap to, and the rule error strings are not even
        // rendered for those customers (theme.liquid only emits quantityRuleStrings for B2B) — so this
        // path would clamp the quantity with no explanation. Post what was typed and let the server
        // answer, exactly as this list did before quantity rules existed.
        if (!window.Maximize.isB2BCustomer) {
          this.updateQty(variantId, parseInt(inputEl.value, 10) || 0);
          return;
        }

        const rule = this._ruleFor(variantId);
        const max = this.getVariantMaxQty(variantId);
        const typed = parseInt(inputEl.value, 10);

        if (typed === 0) {
          this._clearQtyRuleError(variantId);
          this.updateQty(variantId, 0);
          return;
        }

        if (quantityRuleUtils.isValid(typed, rule) && typed <= max) {
          this._clearQtyRuleError(variantId);
          this.updateQty(variantId, typed);
          return;
        }

        const message = quantityRuleUtils.buildError(inputEl.value, rule); // from the typed value
        const belowMin = !Number.isInteger(typed) || typed < rule.min;
        const aboveMax = typed > max || (rule.max !== null && typed > rule.max);

        if (belowMin || aboveMax) {
          let corrected = rule.min;
          if (aboveMax) {
            // Step DOWN onto the grid — rounding could land above an inventory cap the rule itself
            // knows nothing about.
            corrected = rule.min + Math.floor((max - rule.min) / rule.increment) * rule.increment;
            if (corrected < rule.min) corrected = rule.min;
          }
          inputEl.value = corrected;
          this._addQtyRuleError(variantId, message, 3000);
          this.updateQty(variantId, corrected);
          return;
        }

        // In range, off the increment grid: say which values are valid and commit nothing.
        this._addQtyRuleError(variantId, message);
      }

      _buildPagination() {
        const list = this.querySelector(".qol-pagination-list");
        if (!list || this.totalPages <= 1) return;

        const nextLi = list.querySelector(".qol-page-btn--next")?.closest("li");

        for (let page = 1; page <= this.totalPages; page++) {
          const li = document.createElement("li");
          const btn = document.createElement("button");
          btn.type = "button";
          btn.className = "qol-page-btn qol-page-item";
          btn.dataset.page = page;
          btn.textContent = page;
          li.appendChild(btn);
          if (nextLi) {
            list.insertBefore(li, nextLi);
          } else {
            list.appendChild(li);
          }
        }
      }

      _renderAll() {
        this._renderRows();
        this._renderPagination();
        this._renderSummary();
        this.productVariants.forEach((v) => this._renderVariant(v.id));
      }

      _renderRows() {
        this.querySelectorAll(".qol-row").forEach((row) => {
          if (!row.dataset.page) {
            row.style.display = "";
            return;
          }
          const page = parseInt(row.dataset.page, 10);
          row.style.display = page === this.currentPage ? "" : "none";
        });
      }

      _renderPagination() {
        const paginationEl = this.querySelector(".qol-pagination");
        if (!paginationEl) return;

        if (this.totalPages > 1) {
          paginationEl.classList.remove("hidden");
          paginationEl.classList.add('border-t');
        } else {
          paginationEl.classList.add("hidden");
          paginationEl.classList.remove("border-t");
        }

        const prevBtn = this.querySelector(".qol-page-btn--prev");
        const nextBtn = this.querySelector(".qol-page-btn--next");

        if (prevBtn) {
          prevBtn.disabled = this.currentPage === 1;
          prevBtn.classList.toggle("is-disabled", this.currentPage === 1);
        }
        if (nextBtn) {
          nextBtn.disabled = this.currentPage === this.totalPages;
          nextBtn.classList.toggle("is-disabled", this.currentPage === this.totalPages);
        }

        this.querySelectorAll(".qol-page-item").forEach((btn) => {
          const page = parseInt(btn.dataset.page, 10);
          btn.classList.toggle("is-active", page === this.currentPage);
          btn.setAttribute("aria-current", page === this.currentPage ? "page" : "false");
        });
      }

      _renderSummary() {
        const totalItemsEl = this.querySelector(".qol-total-items");
        if (totalItemsEl) totalItemsEl.textContent = this.totalItems;

        const subtotalEl = this.querySelector(".qol-subtotal");
        if (subtotalEl) subtotalEl.innerHTML = window.formatMoney(this.subtotal, this.moneyFormat);

        const removeAllBtn = this.querySelector(".qol-remove-all-link");
        if (removeAllBtn) removeAllBtn.style.display = this.totalItems > 0 ? "" : "none";

        const summaryPanel = this.querySelector(".qol-summary");
        if (summaryPanel) summaryPanel.classList.toggle("is-bursting", this.removeAllBursting);

        const summaryContent = this.querySelector(".qol-summary-content");
        if (summaryContent) {
          summaryContent.classList.toggle("is-leaving", this.removeAllBursting || this.removeAllConfirm);
        }

        const summaryConfirm = this.querySelector(".quick-order-list__summary-confirm");
        if (summaryConfirm) {
          summaryConfirm.classList.toggle("is-visible", this.removeAllConfirm);
          summaryConfirm.setAttribute("aria-hidden", String(!this.removeAllConfirm));
        }

        const confirmCount = this.querySelector(".qol-confirm-count");
        if (confirmCount) confirmCount.textContent = this.totalItems;

        const confirmBtn = this.querySelector(".qol-confirm-btn");
        const cancelBtn = this.querySelector(".qol-cancel-link");
        const spinner = this.querySelector(".qol-spinner");
        const confirmBtnText = this.querySelector(".qol-confirm-btn-text");

        if (confirmBtn) {
          confirmBtn.classList.toggle("is-loading", this.isRemovingAll);
          confirmBtn.setAttribute("aria-disabled", String(this.isRemovingAll));
        }
        if (cancelBtn) {
          cancelBtn.disabled = this.isRemovingAll;
          cancelBtn.classList.toggle("is-disabled", this.isRemovingAll);
        }
        if (spinner) spinner.classList.toggle("hidden", !this.isRemovingAll);
        if (confirmBtnText) confirmBtnText.classList.toggle("invisible", this.isRemovingAll);

        this.classList.toggle("is-removing-all", this.isRemovingAll);
      }

      _renderVariant(variantId) {
        const qty = this.getVariantQty(variantId);
        const variant = this.productVariants.find((v) => v.id === variantId);
        // Effective per-unit price at the current qty (applies volume-pricing tiers).
        const unitPrice = variant ? quantityRuleUtils.priceForQuantity(variant.price, variant.priceBreaks, qty) : 0;

        this.querySelectorAll(`.qol-qty-input[data-variant-id="${variantId}"]`).forEach((input) => {
          if (document.activeElement !== input) input.value = qty;
        });

        this.querySelectorAll(`.qol-remove-btn[data-variant-id="${variantId}"]`).forEach((btn) => {
          btn.style.display = qty > 0 ? "" : "none";
        });

        const maxQty = this.getVariantMaxQty(variantId);
        const isAtMax = maxQty !== Infinity && qty >= maxQty;
        this.querySelectorAll(`.qol-qty-increment[data-variant-id="${variantId}"]`).forEach((el) => {
          if (!this._updatingVariants.has(variantId)) {
            el.setAttribute("aria-disabled", String(isAtMax));
            el.tabIndex = isAtMax ? -1 : 0;
          }
          el.classList.toggle("is-disabled", isAtMax);
        });

        // Only write the displayed total when the variant is neither waiting on its debounce
        // nor mid-request — the confirmed value is revealed by _setVariantUpdating(false).
        if (!this._pendingVariants.has(variantId) && !this._updatingVariants.has(variantId)) {
          this.querySelectorAll(`.qol-variant-total[data-variant-id="${variantId}"]`).forEach((el) => {
            el.innerHTML = window.formatMoney(qty * unitPrice, this.moneyFormat);
            el.classList.toggle("qol-variant-total--active", qty > 0);
          });
          // Per-each price column follows the volume tier as qty changes.
          this.querySelectorAll(`.qol-price-each-amount[data-variant-id="${variantId}"]`).forEach((el) => {
            el.innerHTML = window.formatMoney(unitPrice, this.moneyFormat);
          });
        }

        const successText = qty === 0 ? this.dataset.itemRemoved || "" : `${qty} ${qty === 1 ? this.dataset.itemInCart || "" : this.dataset.itemsInCart || ""}`;
        this.querySelectorAll(`.flash-message.success-message[data-variant-id="${variantId}"]`).forEach((el) => {
          el.classList.toggle("hidden", !this.showSuccess[variantId]);
          const textEl = el.querySelector(".qol-success-text");
          if (textEl) textEl.textContent = successText;
        });

        const msg = this.errorMessages[variantId] || "";
        this.querySelectorAll(`.flash-message.error-message[data-variant-id="${variantId}"]`).forEach((el) => {
          el.classList.toggle("hidden", !msg);
          if (msg) el.textContent = msg;
        });
      }

      async fetchCart() {
        const cart = await fetch(window.Shopify.routes.root + "cart.js").then((r) => r.json());
        this._applyCartState(cart.items);
        return cart;
      }

      async _syncCartFromServer() {
        const { sectionsToRender, sections } = this._getCartSections();
        const cart = await fetch(window.Shopify.routes.root + "cart.js").then((r) => r.json());

        if (!sections.length) {
          this._applyCartState(cart.items);
          this._notifyCartUpdate();
          return cart;
        }

        const updates = {};
        cart.items.forEach((item) => {
          updates[item.variant_id] = item.quantity;
        });

        const data = await fetch(window.Shopify.routes.root + "cart/update.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ updates, sections }),
        }).then((r) => r.json());

        this._handleCartResponse(data, sectionsToRender);
        return data;
      }

      syncServerQty(items) {
        this.serverQty = {};
        items.forEach((item) => {
          if (this.serverQty[item.variant_id]) {
            this.serverQty[item.variant_id] = this.serverQty[item.variant_id] + item.quantity;
          } else {
            this.serverQty[item.variant_id] = item.quantity;
          }
        });
      }

      getServerQty(variantId) {
        return this.serverQty[variantId] || 0;
      }

      getVariantQty(variantId) {
        let quantity = 0;

        const items = this.cartItems.filter((i) => i.variant_id === variantId);
        items.forEach((item) => {
          quantity += item.quantity;
        });

        return quantity;
      }

      isVariantPreorder(variantId) {
        return this.variantPreorder[variantId] === true;
      }

      getVariantMaxQty(variantId) {
        const variant = this.productVariants.find((v) => v.id === variantId);
        if (!variant) return Infinity;
        // A B2B maximum applies even to pre-order / continue-selling variants, so it is
        // checked before the inventory rules below.
        const ruleMax = this._ruleFor(variantId).max;
        const inventoryMax = this.__inventoryMaxQty(variantId);
        if (ruleMax === null) return inventoryMax;
        return Math.min(ruleMax, inventoryMax);
      }

      __inventoryMaxQty(variantId) {
        const variant = this.productVariants.find((v) => v.id === variantId);
        if (!variant) return Infinity;
        if (this.isVariantPreorder(variantId)) return Infinity;
        if (variant.inventory_management === "shopify" && variant.inventory_policy === "deny") {
          return variant.inventory_quantity;
        }
        return Infinity;
      }

      // Shopify quantity rule (min / max / increment) for a variant. This list used to ignore
      // rules entirely — it stepped by 1, defaulted to whatever was in the cart and produced no
      // increment-range message — so every rule-aware behaviour below hangs off this.
      _ruleFor(variantId) {
        const variant = this.productVariants.find((v) => v.id === variantId);
        const qr = (variant && variant.quantity_rule) || {};
        return quantityRuleUtils.normalizeRule(qr.min, qr.max, qr.increment);
      }

      calculateTotals() {
        let itemsCount = 0;
        let total = 0;
        this.productVariants.forEach((variant) => {
          const qty = this.getVariantQty(variant.id);
          if (qty > 0) {
            itemsCount += qty;
            total += qty * quantityRuleUtils.priceForQuantity(variant.price, variant.priceBreaks, qty);
          }
        });
        this.totalItems = itemsCount;
        this.subtotal = total;
      }

      _getCartSections() {
        let cartSectionEl = document.querySelector("#CartDrawer");
        let cartSectionId = cartSectionEl?.dataset.sectionId || false;
        if (window.Maximize.isCartPage) {
          cartSectionEl = document.querySelector("#MainCart__Items");
          cartSectionId = cartSectionEl?.dataset.sectionId || false;
        }
        const sectionsToRender = window.xCartHelper?.getSectionsToRender(cartSectionId) || [];
        return { sectionsToRender, sections: sectionsToRender.map((s) => s.id) };
      }

      _renderCartSections(sectionsToRender, sections) {
        if (!sections) return;
        sectionsToRender.forEach((section) => {
          const el = document.querySelector(section.selector);
          if (!el || !sections[section.id]) return;
          const html = getSectionInnerHTML(sections[section.id], section.selector);
          if (section.selector === "#CartDrawer") {
            const wrapperVisible = el.querySelector("#CartDrawer__Wrapper")?.classList.contains("is-visible");
            el.innerHTML = html ?? "";
            if (wrapperVisible) el.querySelector("#CartDrawer__Wrapper")?.classList.add("is-visible");
          } else {
            el.innerHTML = html ?? "";
          }
        });
      }

      _applyCartState(items) {
        this.cartItems = items;
        this.syncServerQty(items);
        this.calculateTotals();
        this._renderAll();
      }

      _notifyCartUpdate(detail = { isItemCountChanged: true }) {
        window.xCartHelper?.updateCurrentCountItem?.();
        document.dispatchEvent(new CustomEvent(CART_EVENT.cartUpdate, { bubbles: true, detail }));
      }

      _handleCartResponse(cart, sectionsToRender) {
        this._renderCartSections(sectionsToRender, cart.sections);
        this._applyCartState(cart.items);
        this._notifyCartUpdate();
      }

      updateQty(variantId, newQty) {
        if (newQty < 0) newQty = 0;

        this._targetQty[variantId] = newQty;

        const idx = this.cartItems.findIndex((i) => i.variant_id === variantId);
        if (idx !== -1) {
          this.cartItems[idx].quantity = newQty;
        } else if (newQty > 0) {
          this.cartItems.push({ variant_id: variantId, quantity: newQty });
        }

        this.calculateTotals();
        this.showSuccess[variantId] = false;
        if (this.successTimers[variantId]) clearTimeout(this.successTimers[variantId]);
        this._pendingVariants.add(variantId); // total deferred until the API confirms
        this.errorMessages[variantId] = "";
        this._renderVariant(variantId);
        this._renderSummary();

        if (!this._debouncedQtyFlush[variantId]) {
          this._debouncedQtyFlush[variantId] = debounce(() => this._scheduleQtySync(variantId), 1000);
        }
        this._debouncedQtyFlush[variantId]();
      }

      _scheduleQtySync(variantId) {
        if (!this._qtyChains[variantId]) this._qtyChains[variantId] = Promise.resolve();
        this._qtyChains[variantId] = this._qtyChains[variantId].then(() => this._performQtyUpdate(variantId, this._targetQty[variantId])).catch((err) => console.error("Quick order list – update error:", err));
      }

      async _cartUpdate(updates, sections) {
        const response = await fetch(window.Shopify.routes.root + "cart/update.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ updates, sections }),
        });
        return response.json();
      }

      _setVariantUpdating(variantId, updating) {
        if (updating) {
          this._updatingVariants.add(variantId);
        } else {
          // Clear BOTH sets before re-rendering so _renderVariant writes the confirmed total.
          // It runs while the total is still invisible, so nothing flashes.
          this._updatingVariants.delete(variantId);
          this._pendingVariants.delete(variantId);
          this._renderVariant(variantId);
        }
        const row = this.querySelector(`.qol-row[data-variant-id="${variantId}"]`);
        row?.classList.toggle("is-updating", updating);
        this.querySelectorAll(`.qol-qty-input[data-variant-id="${variantId}"]`).forEach((el) => {
          el.disabled = updating;
        });
        this.querySelectorAll(`.qol-remove-btn[data-variant-id="${variantId}"]`).forEach((el) => {
          el.disabled = updating;
        });
        this.querySelectorAll(`.qol-qty-decrement[data-variant-id="${variantId}"], .qol-qty-increment[data-variant-id="${variantId}"]`).forEach((el) => {
          const isIncrement = el.classList.contains("qol-qty-increment");
          const atMax = !updating && isIncrement && this.getVariantQty(variantId) >= this.getVariantMaxQty(variantId);
          el.setAttribute("aria-disabled", String(updating || atMax));
          el.tabIndex = updating || atMax ? -1 : 0;
          if (isIncrement) el.classList.toggle("is-disabled", atMax);
        });

        // Spinner in place of the row total while the request is out.
        this.querySelectorAll(`.qol-col-total[data-variant-id="${variantId}"] .qol-total-spinner`).forEach((el) => {
          el.classList.toggle("hidden", !updating);
        });
        this.querySelectorAll(`.qol-variant-total[data-variant-id="${variantId}"]`).forEach((el) => {
          el.classList.toggle("invisible", updating);
        });
      }

      async _performQtyUpdate(variantId, newQty) {
        const qtyAtStart = this._targetQty[variantId];
        this._activeQtyUpdates++;
        this.isUpdating = true;
        window.xCartHelper?.beginUpdate();
        this._setVariantUpdating(variantId, true);
        this.errorMessages[variantId] = "";

        const { sectionsToRender, sections } = this._getCartSections();
        const confirmedQty = this.getServerQty(variantId);
        const delta = newQty - confirmedQty;

        if (delta === 0) {
          this._setVariantUpdating(variantId, false);
          this._endQtyUpdate();
          return;
        }

        // Carry the pre-order line-item property through, so an added pre-order row keeps its note.
        let formData = {
          id: variantId,
          quantity: delta,
        };
        const preOrderMessageEle = this.querySelector(`.qol-row__preorder__${variantId}`);
        if (preOrderMessageEle) {
          formData["properties"] = {
            [preOrderMessageEle.dataset.preOrderName]: preOrderMessageEle.dataset.preOrderValue,
          };
        }

        try {
          let data;

          if (delta > 0) {
            const addData = await fetch(window.Shopify.routes.root + "cart/add.js", {
              method: "POST",
              headers: { "Content-Type": "application/json", Accept: "application/json" },
              body: JSON.stringify({ items: [formData], sections }),
            }).then((r) => r.json());

            // if (addData.status === 422 && !this.isVariantPreorder(variantId)) {
            if (addData.status === 422) {
              this.errorMessages[variantId] = addData.description || addData.message || "Unable to add item.";
              this._renderVariant(variantId);
              this._autoHideOnMobile(variantId);
              await this._syncCartFromServer();
              return;
            }
            // Apply cart section HTML immediately, show success — don't wait for /cart.js
            this._renderCartSections(sectionsToRender, addData.sections || {});
            this.serverQty[variantId] = (this.serverQty[variantId] || 0) + delta;
            this._notifyCartUpdate();
            if (newQty > 0) this._showSuccessMessage(variantId);

            // Sync accurate cart state in background
            fetch(window.Shopify.routes.root + "cart.js")
              .then((r) => r.json())
              .then((cart) => this._applyCartState(cart.items))
              .catch(() => {});
            return;
          } else {
            data = await this._cartUpdate({ [variantId]: newQty }, sections);
          }

          this._handleCartResponse(data, sectionsToRender);
          this._showSuccessMessage(variantId);
        } catch (err) {
          console.error("Quick order list – update error:", err);
          this.errorMessages[variantId] = "An error occurred. Please try again.";
          this._renderVariant(variantId);
          this._autoHideOnMobile(variantId);
          await this._syncCartFromServer();
        } finally {
          this._setVariantUpdating(variantId, false);
          this._endQtyUpdate();
          if (this._targetQty[variantId] !== qtyAtStart) this._scheduleQtySync(variantId);
        }
      }

      _endQtyUpdate() {
        this._activeQtyUpdates = Math.max(0, this._activeQtyUpdates - 1);
        this.isUpdating = this._activeQtyUpdates > 0;
        window.xCartHelper?.endUpdate();
      }

      onRemoveAllClick() {
        if (this.removeAllBursting || this.removeAllConfirm || this.totalItems <= 0) return;
        clearTimeout(this._removeAllBurstTimer);
        clearTimeout(this._removeAllConfirmRevealTimer);

        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
          this.removeAllConfirm = true;
          this._renderSummary();
          return;
        }

        const btn = this.querySelector(".qol-remove-all-link");
        const summary = this.querySelector(".qol-summary");
        if (btn && summary) {
          const rgb = this._parseRgb(getComputedStyle(btn).color);
          if (rgb) summary.style.setProperty("--burst-color", `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.06)`);

          const summaryRect = summary.getBoundingClientRect();
          const btnRect = btn.getBoundingClientRect();
          const originX = btnRect.left + btnRect.width / 2 - summaryRect.left;
          const originY = btnRect.top + btnRect.height / 2 - summaryRect.top;
          const w = summaryRect.width;
          const h = summaryRect.height;
          const maxDist = Math.max(Math.hypot(originX, originY), Math.hypot(w - originX, originY), Math.hypot(originX, h - originY), Math.hypot(w - originX, h - originY));
          summary.style.setProperty("--burst-x", `${originX}px`);
          summary.style.setProperty("--burst-y", `${originY}px`);
          summary.style.setProperty("--burst-size", `${maxDist * 2}px`);
        }

        this.removeAllBursting = true;
        this.removeAllConfirm = false;
        this._renderSummary();

        this._removeAllConfirmRevealTimer = setTimeout(() => {
          requestAnimationFrame(() => {
            this.removeAllConfirm = true;
            this._renderSummary();
          });
        }, 340);
        this._removeAllBurstTimer = setTimeout(() => this._finishRemoveAllBurst(), 520);
      }

      _finishRemoveAllBurst() {
        if (!this.removeAllBursting) return;
        clearTimeout(this._removeAllBurstTimer);
        this.removeAllBursting = false;
        this._renderSummary();
      }

      closeRemoveAllConfirm() {
        if (this.isRemovingAll) return;
        clearTimeout(this._removeAllConfirmRevealTimer);
        this.removeAllConfirm = false;
        this._renderSummary();
      }

      _parseRgb(color) {
        const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
        if (!match) return null;
        return { r: +match[1], g: +match[2], b: +match[3] };
      }

      async removeAll() {
        this.isUpdating = true;
        this.isRemovingAll = true;
        window.xCartHelper?.beginUpdate();
        this._renderSummary();

        const updates = {};
        this.productVariants.forEach((variant) => {
          if (this.getVariantQty(variant.id) > 0) updates[variant.id] = 0;
        });

        const { sectionsToRender, sections } = this._getCartSections();
        try {
          const cart = await this._cartUpdate(updates, sections);
          this._handleCartResponse(cart, sectionsToRender);
        } catch (err) {
          console.error("Quick order list – remove all error:", err);
        } finally {
          window.xCartHelper?.endUpdate();
          this.isUpdating = false;
          this.isRemovingAll = false;
          this.removeAllConfirm = false;
          this._renderSummary();
        }
      }

      _showSuccessMessage(variantId) {
        this.showSuccess[variantId] = true;
        this._renderVariant(variantId);
        if (this.successTimers[variantId]) clearTimeout(this.successTimers[variantId]);
        const delay = window.innerWidth < 768 ? 3000 : 5000;
        this.successTimers[variantId] = setTimeout(() => this._hideToast(variantId), delay);
      }

      _autoHideOnMobile(variantId) {
        if (window.innerWidth >= 768) return;
        if (this.successTimers[variantId]) clearTimeout(this.successTimers[variantId]);
        this.successTimers[variantId] = setTimeout(() => this._hideToast(variantId), 3000);
      }

      _hideToast(variantId) {
        clearTimeout(this.successTimers[variantId]);
        this.showSuccess[variantId] = false;
        this.errorMessages[variantId] = "";
        this._renderVariant(variantId);
      }

      _handleDocumentClick(e) {
        this.querySelectorAll(".flash-message.success-message:not(.hidden), .flash-message.error-message:not(.hidden)").forEach((el) => {
          if (!el.contains(e.target)) {
            this._hideToast(parseInt(el.dataset.variantId, 10));
          }
        });
      }

      viewCart() {
        const cartDrawerEl = document.getElementById("CartDrawer");
        if (cartDrawerEl && window.Maximize.isEnableCartDrawer) {
          cartDrawerEl.returnFocusEl = this.querySelector(".qol-view-cart-btn");
          cartDrawerEl.isOpen = true;
          document.getElementById("CartDrawer__Wrapper")?.classList.add("is-visible");
        } else {
          window.location.href = window.Shopify.routes.root + "cart";
        }
      }

      changePage(page) {
        if (page >= 1 && page <= this.totalPages) {
          this.currentPage = page;
          this._renderRows();
          this._renderPagination();
        }
      }

      async _navigatePage(href) {
        const sectionId = this.dataset.sectionId;
        if (!sectionId) {
          window.location.href = href;
          return;
        }

        this.classList.add("qol-loading");

        try {
          const fetchUrl = new URL(href, window.location.origin);
          fetchUrl.searchParams.set("sections", sectionId);

          const response = await fetch(fetchUrl.toString());
          if (!response.ok) throw new Error(`Fetch failed: ${response.status}`);

          const data = await response.json();
          const sectionHtml = data[sectionId];
          if (!sectionHtml) throw new Error("Section not found in response");

          const parser = new DOMParser();
          const doc = parser.parseFromString(sectionHtml, "text/html");

          const currentTbody = this.querySelector(".qol-tbody");
          const newTbody = doc.querySelector(".qol-tbody");
          if (currentTbody && newTbody) currentTbody.replaceWith(newTbody);

          const currentPagination = this.querySelector(".qol-pagination");
          const newPagination = doc.querySelector(".qol-pagination");
          if (currentPagination && newPagination) currentPagination.replaceWith(newPagination);

          this.querySelectorAll('.qol-row[data-preorder="true"]').forEach((row) => {
            this.variantPreorder[parseInt(row.dataset.variantId, 10)] = true;
          });

          this._renderAll();

          this.scrollIntoView({ behavior: "smooth", block: "start" });
        } catch (err) {
          console.error("Quick order list – page navigation error:", err);
          window.location.href = href;
        } finally {
          this.classList.remove("qol-loading");
        }
      }
    }

    customElements.define("quick-order-list", QuickOrderList);
  });
}
